import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, ArrowRight, ArrowLeft, CheckCircle2, Crown, Store, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth, DASHBOARD_HOME } from "../context/AuthContext";
import api, { errMsg } from "../lib/api";
import BrandLogo from "../components/BrandLogo";
import PresetSwitcher, { applyPreset, initPreset } from "../components/PresetSwitcher";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const BG = "https://images.pexels.com/photos/11382581/pexels-photo-11382581.jpeg";

const ROLE_COPY = {
  PARTNER: { icon: Users, title: "Partner Login", sub: "Create and manage client websites & stores.", allowRegister: false },
  BUSINESS_OWNER: { icon: Store, title: "Business Owner Login", sub: "Manage your store, products, orders and customers.", allowRegister: false },
  SUPER_ADMIN: { icon: Crown, title: "Super Admin Console", sub: "Platform-wide control: partners, pricing, themes, payments.", allowRegister: false },
  ROOT_SUPER_ADMIN: { icon: Crown, title: "Root Super Admin", sub: "Highest-level platform ownership — super admins, plans, full oversight.", allowRegister: false },
};

function Field({ label, ...props }) {
  return (
    <div className="space-y-1.5">
      <Label className="overline-label">{label}</Label>
      <Input {...props} className="h-12 rounded-md bg-background" />
    </div>
  );
}

export default function Login({ role = "PARTNER", initialMode = "login" }) {
  const copy = ROLE_COPY[role] || ROLE_COPY.PARTNER;
  const [mode, setMode] = useState(initialMode); // login | register | forgot
  const [form, setForm] = useState({ name: "", company: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [preset, setPreset] = useState(initPreset);
  const { login, register, setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { setMode(initialMode); }, [role, initialMode]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        const user = await login(form.email, form.password);
        toast.success(`Welcome back, ${user.name}`);
        if (user.role === "RE_MEMBER") {
          // Real Estate org members (Director/Manager/Client) — one common login, routed to their RE portal
          const t = localStorage.getItem("zylo_token");
          localStorage.removeItem("zylo_token");
          setUser(null);
          if (user.re_slug) {
            localStorage.setItem(`zylo_remember_${user.re_slug}`, t);
            localStorage.setItem(`zylo_remember_${user.re_slug}_info`, JSON.stringify({ id: user.id, full_name: user.name, email: user.email, role: user.re_role }));
          }
          navigate(user.re_slug ? `/site/${user.re_slug}/re/org` : "/");
          return;
        }
        if (user.role === "BUSINESS_OWNER" && user.self_service) navigate("/self-service/setup");
        else navigate(DASHBOARD_HOME[user.role] || "/");
      } else if (mode === "register") {
        const user = await register({ name: form.name, company: form.company, email: form.email, password: form.password });
        toast.success("Partner account created — your free trial has started");
        navigate(DASHBOARD_HOME[user.role]);
      } else {
        const { data } = await api.post("/auth/forgot-password", { email: form.email });
        toast.success(data.message);
        if (data.dev_reset_link) navigate(data.dev_reset_link);
      }
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background" data-testid={`login-${role.toLowerCase()}`}>
      <div className="flex flex-col justify-center px-6 sm:px-16 lg:px-24 py-16 relative">
        <Link to="/" data-testid="back-home" className="absolute top-6 left-6 sm:left-16 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-4 w-4" /> Home
        </Link>
        <div className="absolute top-6 right-6">
          <PresetSwitcher value={preset} onChange={(p) => { setPreset(p); applyPreset(p); }} />
        </div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md mx-auto lg:mx-0">
          <div className="mb-10">
            <BrandLogo size="lg" />
          </div>
          <div className="inline-flex items-center gap-2 glass-card rounded-full px-4 py-2 text-xs font-bold mb-6">
            <copy.icon className="h-3.5 w-3.5 text-accent" /> {copy.title}
          </div>
          <h1 data-testid="login-heading" className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter leading-[1.05]">
            {mode === "register" ? "Start building for clients." : mode === "forgot" ? "Reset your password." : <>Welcome <span className="text-gradient">back.</span></>}
          </h1>
          <p className="text-muted-foreground mt-4 mb-10">{mode === "register" ? "Create a partner account and launch client websites in minutes." : copy.sub}</p>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <>
                <Field label="Full name" data-testid="register-name" value={form.name} required
                  onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Aarav Sharma" />
                <Field label="Company / Agency" data-testid="register-company" value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Skyline Web Studio" />
              </>
            )}
            <Field label="Email" type="email" data-testid="login-email" value={form.email} required
              onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" />
            {mode !== "forgot" && (
              <Field label="Password" type="password" data-testid="login-password" value={form.password} required
                onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" />
            )}
            <Button type="submit" disabled={busy} data-testid="auth-submit-button"
              className="w-full h-12 rounded-full font-bold text-base gradient-bg text-white border-0 glow-sm active:scale-95 transition-transform">
              {busy ? "Please wait…" : mode === "login" ? "Sign in" : mode === "register" ? "Create partner account" : "Send reset link"}
              {!busy && <ArrowRight className="h-4 w-4 ml-2" />}
            </Button>
          </form>

          <div className="mt-8 space-y-2 text-sm">
            {mode !== "login" && (
              <button data-testid="switch-to-login" onClick={() => setMode("login")} className="block text-muted-foreground hover:text-foreground transition-colors">
                Already have an account? <span className="font-bold text-foreground">Sign in</span>
              </button>
            )}
            {mode === "login" && (
              <>
                {copy.allowRegister && (
                  <button data-testid="switch-to-register" onClick={() => setMode("register")} className="block text-muted-foreground hover:text-foreground transition-colors">
                    New partner? <span className="font-bold text-foreground">Create an account</span>
                  </button>
                )}
                <button data-testid="switch-to-forgot" onClick={() => setMode("forgot")} className="block text-muted-foreground hover:text-foreground transition-colors">
                  Forgot your password?
                </button>
                <div className="pt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  {role !== "PARTNER" && <Link to="/login" className="hover:text-foreground transition-colors">Partner login</Link>}
                  {role !== "BUSINESS_OWNER" && <Link to="/business-owner/login" className="hover:text-foreground transition-colors">Business owner login</Link>}
                  {role !== "SUPER_ADMIN" && <Link to="/super-admin/login" className="hover:text-foreground transition-colors">Super admin login</Link>}
                  {role !== "ROOT_SUPER_ADMIN" && <Link to="/root-admin/login" className="hover:text-foreground transition-colors">Root admin login</Link>}
                </div>
              </>
            )}
          </div>
        </motion.div>
      </div>

      <div className="hidden lg:block relative overflow-hidden">
        <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-black/70 via-black/40 to-transparent" />
        <div className="absolute inset-0 hero-aurora opacity-60" />
        <div className="relative h-full flex flex-col justify-end p-16 text-white">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <p className="overline-label text-white/60 mb-4">Partner-powered SaaS</p>
            <h2 className="font-display font-extrabold text-5xl tracking-tighter leading-[1.05] max-w-md">
              Websites & stores your <span className="text-gradient">clients will love.</span>
            </h2>
            <div className="mt-8 space-y-3">
              {["Launch client sites in a 5-step wizard", "Per-website pricing that scales with you", "Full store admin for every business owner"].map((t) => (
                <div key={t} className="flex items-center gap-3 text-white/85">
                  <CheckCircle2 className="h-4 w-4 text-emerald-400" /> <span className="text-sm font-medium">{t}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  useEffect(() => { initPreset(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token: params.get("token") || "", password });
      toast.success("Password updated — sign in with your new password");
      navigate("/login");
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6 hero-aurora">
      <form onSubmit={submit} className="w-full max-w-sm space-y-4 glass-card rounded-2xl p-8 glow-sm" data-testid="reset-form">
        <h1 className="font-display font-extrabold text-3xl tracking-tight">New password</h1>
        <Field label="New password" type="password" data-testid="reset-password-input" value={password} required onChange={(e) => setPassword(e.target.value)} />
        <Button data-testid="reset-submit-button" disabled={busy} className="w-full h-12 rounded-full font-bold gradient-bg text-white border-0">Update password</Button>
      </form>
    </div>
  );
}
